class PredictiveSearch extends SearchForm {
  constructor() {
    super();

    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.emptyDiv = this.querySelector('.predictive-search-empty');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');

    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener(
      'submit',
      this.onFormSubmit.bind(this)
    );

    this.input.addEventListener(
      'focus',
      this.onFocus.bind(this)
    );

    this.addEventListener(
      'focusout',
      this.onFocusOut.bind(this)
    );

    this.addEventListener(
      'keyup',
      this.onKeyup.bind(this)
    );

    this.addEventListener(
      'keydown',
      this.onKeydown.bind(this)
    );

    /*
     * Handle clicks outside the predictive search.
     *
     * If the user clicks anywhere outside the search
     * component, close the search drawer.
     *
     * If the user clicks inside the search/product grid,
     * keep it open.
     */
    document.addEventListener(
      'click',
      this.onDocumentClick.bind(this)
    );
  }

  getQuery() {
    return this.input.value.trim();
  }

  isSearchDrawerActive() {
    return (
      this.isOpen ||
      this.classList.contains('active-search') ||
      this.hasAttribute('open')
    );
  }

  /*
   * Keep drawer open only for the search field and in-drawer product grids.
   */
  shouldKeepSearchOpenOnClick(target) {
    if (!this.contains(target)) {
      return false;
    }

    if (target.closest('.field')) {
      return true;
    }

    if (
      target.closest(
        '.popular_items_slider_search, .predictive-search__results-list-product'
      )
    ) {
      return true;
    }

    return false;
  }

  dismissSearchDrawer(clearSearchTerm = false) {
    this.close(clearSearchTerm);
    this.classList.remove('active-search');

    if (this.emptyDiv) {
      this.emptyDiv.removeAttribute('style');
    }

    if (this.contains(document.activeElement)) {
      this.input.blur();
    }
  }

  /*
   * Close search when clicking outside the product grid / search field.
   */
  onDocumentClick(event) {
    if (!this.isSearchDrawerActive()) {
      return;
    }

    if (this.shouldKeepSearchOpenOnClick(event.target)) {
      return;
    }

    this.dismissSearchDrawer();
  }

  onChange() {
    super.onChange();

    const newSearchTerm = this.getQuery();

    if (
      !this.searchTerm ||
      !newSearchTerm.startsWith(this.searchTerm)
    ) {
      this.querySelector(
        '#predictive-search-results-groups-wrapper'
      )?.remove();
    }

    this.updateSearchForTerm(
      this.searchTerm,
      newSearchTerm
    );

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (
      !this.getQuery().length ||
      this.querySelector('[aria-selected="true"] a')
    ) {
      event.preventDefault();
    }
  }

  onFormReset(event) {
    super.onFormReset(event);

    if (super.shouldResetForm()) {
      this.searchTerm = '';

      this.abortController.abort();
      this.abortController = new AbortController();

      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!this.searchTerm.length) {
      this.classList.add('active-search');

      if (this.emptyDiv) {
        this.emptyDiv.style.maxHeight =
          `${this.getResultsMaxHeight()}px`;
      }
    }

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (
      this.getAttribute('results') === 'true'
    ) {
      this.open();
    } else {
      this.getSearchResults(
        this.searchTerm
      );
    }
  }

  onFocusOut() {
    /*
     * Do not close the search here.
     *
     * Outside clicks are handled by onDocumentClick().
     *
     * This is important for wishlist buttons because
     * wishlist apps can move focus outside the search
     * component.
     */
  }

  onKeyup(event) {
    if (!this.getQuery().length) {
      this.close(true);
    }

    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;

      case 'ArrowDown':
        this.switchOption('down');
        break;

      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (
      event.code === 'ArrowUp' ||
      event.code === 'ArrowDown'
    ) {
      event.preventDefault();
    }
  }

  updateSearchForTerm(
    previousTerm,
    newTerm
  ) {
    const searchForTextElement =
      this.querySelector(
        '[data-predictive-search-search-for-text]'
      );

    const currentButtonText =
      searchForTextElement?.innerText;

    if (currentButtonText) {
      const matches =
        currentButtonText.match(
          new RegExp(previousTerm, 'g')
        );

      if (matches && matches.length > 1) {
        return;
      }

      const newButtonText =
        currentButtonText.replace(
          previousTerm,
          newTerm
        );

      searchForTextElement.innerText =
        newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) {
      return;
    }

    const moveUp =
      direction === 'up';

    const selectedElement =
      this.querySelector(
        '[aria-selected="true"]'
      );

    const allVisibleElements =
      Array.from(
        this.querySelectorAll(
          'li, button.predictive-search__item'
        )
      ).filter(
        (element) =>
          element.offsetParent !== null
      );

    let activeElementIndex = 0;

    if (
      moveUp &&
      !selectedElement
    ) {
      return;
    }

    let selectedElementIndex = -1;
    let i = 0;

    while (
      selectedElementIndex === -1 &&
      i <= allVisibleElements.length
    ) {
      if (
        allVisibleElements[i] ===
        selectedElement
      ) {
        selectedElementIndex = i;
      }

      i++;
    }

    if (this.statusElement) {
      this.statusElement.textContent = '';
    }

    if (
      !moveUp &&
      selectedElement
    ) {
      activeElementIndex =
        selectedElementIndex ===
        allVisibleElements.length - 1
          ? 0
          : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex =
        selectedElementIndex === 0
          ? allVisibleElements.length - 1
          : selectedElementIndex - 1;
    }

    if (
      activeElementIndex ===
      selectedElementIndex
    ) {
      return;
    }

    const activeElement =
      allVisibleElements[
        activeElementIndex
      ];

    if (!activeElement) {
      return;
    }

    activeElement.setAttribute(
      'aria-selected',
      'true'
    );

    if (selectedElement) {
      selectedElement.setAttribute(
        'aria-selected',
        'false'
      );
    }

    this.input.setAttribute(
      'aria-activedescendant',
      activeElement.id
    );
  }

  selectOption() {
    const selectedOption =
      this.querySelector(
        '[aria-selected="true"] a, button[aria-selected="true"]'
      );

    if (selectedOption) {
      selectedOption.click();
    }
  }

  getSearchResults(searchTerm) {
    const queryKey =
      searchTerm
        .replaceAll(' ', '-')
        .toLowerCase();

    this.setLiveRegionLoadingState();

    if (
      this.cachedResults[queryKey]
    ) {
      this.renderSearchResults(
        this.cachedResults[queryKey]
      );

      return;
    }

    fetch(
      `${routes.predictive_search_url}?q=${encodeURIComponent(
        searchTerm
      )}&resources[type]=product,collection,page,article,query&resources[limit_scope]=each&resources[options][unavailable_products]=last&resources[options][prefix]=last&section_id=predictive-search`,
      {
        signal:
          this.abortController.signal,
      }
    )
      .then((response) => {
        if (!response.ok) {
          const error =
            new Error(
              response.status
            );

          this.close();

          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const section =
          new DOMParser()
            .parseFromString(
              text,
              'text/html'
            )
            .querySelector(
              '#shopify-section-predictive-search'
            );

        if (!section) {
          throw new Error(
            'Predictive search section not found'
          );
        }

        const resultsMarkup =
          section.innerHTML;

        this.allPredictiveSearchInstances.forEach(
          (predictiveSearchInstance) => {
            predictiveSearchInstance.cachedResults[
              queryKey
            ] = resultsMarkup;
          }
        );

        this.renderSearchResults(
          resultsMarkup
        );
      })
      .catch((error) => {
        if (error?.code === 20) {
          return;
        }

        this.close();

        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement =
      this.statusElement ||
      this.querySelector(
        '.predictive-search-status'
      );

    this.loadingText =
      this.loadingText ||
      this.getAttribute(
        'data-loading-text'
      );

    if (this.statusElement) {
      this.setLiveRegionText(
        this.loadingText
      );
    }

    this.setAttribute(
      'loading',
      'true'
    );
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) {
      return;
    }

    this.statusElement.setAttribute(
      'aria-hidden',
      'false'
    );

    this.statusElement.textContent =
      statusText;

    setTimeout(() => {
      if (this.statusElement) {
        this.statusElement.setAttribute(
          'aria-hidden',
          'true'
        );
      }
    }, 1000);
  }

  renderSearchResults(
    resultsMarkup
  ) {
    this.predictiveSearchResults.innerHTML =
      resultsMarkup;

    this.setAttribute(
      'results',
      'true'
    );

    this.setLiveRegionResults();

    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute(
      'loading'
    );

    const countElement =
      this.querySelector(
        '[data-predictive-search-live-region-count-value]'
      );

    if (countElement) {
      this.setLiveRegionText(
        countElement.textContent
      );
    }
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight -
      document
        .querySelector(
          '.section-header'
        )
        ?.getBoundingClientRect()
        .bottom;

    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight =
      `${this.getResultsMaxHeight()}px`;

    this.setAttribute(
      'open',
      'true'
    );

    this.input.setAttribute(
      'aria-expanded',
      'true'
    );

    this.isOpen = true;

    if (
      this.getQuery().length === 0
    ) {
      this.classList.add(
        'active-search'
      );
    } else {
      this.classList.remove(
        'active-search'
      );
    }
  }

  close(
    clearSearchTerm = false
  ) {
    this.closeResults(
      clearSearchTerm
    );

    this.isOpen = false;

    if (
      this.getQuery().length === 0
    ) {
      this.classList.add(
        'active-search'
      );
    } else {
      this.classList.remove(
        'active-search'
      );
    }
  }

  closeResults(
    clearSearchTerm = false
  ) {
    if (clearSearchTerm) {
      this.input.value = '';

      this.removeAttribute(
        'results'
      );
    }

    const selected =
      this.querySelector(
        '[aria-selected="true"]'
      );

    if (selected) {
      selected.setAttribute(
        'aria-selected',
        'false'
      );
    }

    this.input.setAttribute(
      'aria-activedescendant',
      ''
    );

    this.removeAttribute(
      'loading'
    );

    this.removeAttribute(
      'open'
    );

    this.input.setAttribute(
      'aria-expanded',
      'false'
    );

    this.resultsMaxHeight = false;

    this.predictiveSearchResults.removeAttribute(
      'style'
    );
  }
}

customElements.define(
  'predictive-search',
  PredictiveSearch
);